// 📁 src/pages/FateFriday.js
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Seo from "../components/seo";
import cardData from "../components/card-data";

/* =========================================================
   THEME + API
========================================================= */

const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE) ||
  "https://tarotfate-api.tarotfate-com.workers.dev";
const WRAP = "px-[1vw]";
const MAXW = "max-w-10xl mx-auto";
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/90";
const BORDER_SOFT = "border-[#005C83]/12";
const PANEL_BASE =
  "relative overflow-hidden rounded-md " +
  BORDER_SOFT +
  " shadow-[0_2px_4px_rgba(0,0,0,0.35)]";

/* Canvas background */
const CANVAS_BG = `
  radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.32), rgba(3,121,155,0) 40%),
  linear-gradient(180deg, #014C75 0%, #005C83 22%, #016280ff 38%, #0C4B84 72%, #014C75 90%, #821E3C 100%)
`;

/* Silk panel + accents */
const SILK_TURQ =
  "bg-[linear-gradient(135deg,rgba(12,63,107,0.40),rgba(20,33,61,0.88))] " +
  "before:absolute before:inset-0 before:bg-[radial-gradient(80%_60%_at_80%_0%,rgba(59,198,214,0.25),transparent_100%)] before:content-['']";

const RIBBON_GRAD =
  "bg-[linear-gradient(90deg,rgba(203,183,234,0.12),rgba(59,198,214,0.18),rgba(203,183,234,0.12))]";

/* Suit accent chips */
const SUIT_STYLE = {
  "Major Arcana":
    "bg-[linear-gradient(135deg,rgba(203,183,234,0.25),rgba(255,255,255,0.06))] border-white/25",
  Wands:
    "bg-[linear-gradient(135deg,rgba(255,140,66,0.28),rgba(255,255,255,0.06))] border-white/25",
  Cups:
    "bg-[linear-gradient(135deg,rgba(112,193,255,0.28),rgba(255,255,255,0.06))] border-white/25",
  Swords:
    "bg-[linear-gradient(135deg,rgba(186,221,255,0.28),rgba(255,255,255,0.06))] border-white/25",
  Pentacles:
    "bg-[linear-gradient(135deg,rgba(185,255,120,0.28),rgba(255,255,255,0.06))] border-white/25",
};

/* =========================================================
   SEO (mirror About.js patterns)
========================================================= */
const siteUrl = "https://tarotfate.com";
const pageUrl = `${siteUrl}/fate-friday`;
const ogImage = `${siteUrl}/icons/og-cover.jpg`;
const orgLogo = `${siteUrl}/icons/logo.webp`;

const metaDescription =
  "Fate Friday: your weekly tarot focus. Every Friday a featured card appears with image, meaning, and a practical 500–700 word guide for action. Explore recent weeks and learn the method.";

const pageKeywords = `
  Fate Friday, weekly tarot, card of the week, Friday tarot, tarot focus,
  major arcana, minor arcana, wands, cups, swords, pentacles, tarot meanings,
  upright meanings, reversed meanings, learn tarot, tarot study, tarot guide,
  spiritual growth, personal development, reflection, mindfulness
`.replace(/\s+/g, " ").trim();

/* =========================================================
   Helpers
========================================================= */
const wc = (s) => (s || "").trim().split(/\s+/).filter(Boolean).length;

function suitSlug(suit) {
  switch (suit) {
    case "Major Arcana":
      return "major-arcana-tarot-suit-cards-meaning";
    case "Wands":
      return "wands-tarot-suit-cards-meaning";
    case "Cups":
      return "cups-tarot-suit-cards-meaning";
    case "Swords":
      return "swords-tarot-suit-cards-meaning";
    case "Pentacles":
      return "pentacles-tarot-suit-cards-meaning";
    default:
      return "tarot-cards";
  }
}

/* Deterministic weekly selection (UTC Friday 09:00) */
function fridayKeyUTC(date = new Date(), hourUTC = 9) {
  const now = date;
  const dow = now.getUTCDay(); // 0 Sun .. 5 Fri .. 6 Sat
  const daysSinceFriday = (7 + dow - 5) % 7;
  const lastFriday = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  );
  lastFriday.setUTCDate(lastFriday.getUTCDate() - daysSinceFriday);
  lastFriday.setUTCHours(hourUTC, 0, 0, 0);
  if (now < lastFriday) lastFriday.setUTCDate(lastFriday.getUTCDate() - 7);
  const epoch = new Date(Date.UTC(2024, 0, 5, hourUTC, 0, 0, 0));
  const weeksSince = Math.floor((lastFriday - epoch) / 604800000);
  return { anchor: lastFriday, weeksSince };
}
function indexForWeek(total, weeksSince) {
  const seed = (0x9e3779b1 ^ (weeksSince * 131) ^ (weeksSince >>> 1)) >>> 0;
  return seed % total;
}
function getISOWeekFromUTC(d) {
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((date - yearStart) / 86400000 + 1) / 7);
  return weekNo;
}
function formatFriday(dt, withWeek = false) {
  const fmt = new Intl.DateTimeFormat(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
  const label = fmt.format(dt);
  if (!withWeek) return label;
  const weekNo = getISOWeekFromUTC(dt);
  return `${label} • Week ${weekNo}`;
}
function pickThisWeek(items) {
  const { anchor, weeksSince } = fridayKeyUTC();
  const idx = indexForWeek(items.length, weeksSince);
  return {
    card: items[idx],
    anchor,
    weeksSince,
    key: `${anchor.toISOString().slice(0, 10)}-Fri`,
  };
}
function getPastWeeks(items, count = 8) {
  const out = [];
  const now = new Date();
  for (let i = 1; i <= count; i++) {
    const past = new Date(now);
    past.setUTCDate(past.getUTCDate() - i * 7);
    const { anchor, weeksSince } = fridayKeyUTC(past);
    const idx = indexForWeek(items.length, weeksSince);
    out.push({
      card: items[idx],
      anchor,
      weeksSince,
      key: `${anchor.toISOString().slice(0, 10)}-Fri`,
    });
  }
  return out;
}

/* =========================================================
   JSON-LD (About.js-style structure)
========================================================= */
function schemaGraph(thisWeek, pastWeeks) {
  const allShown = [thisWeek, ...pastWeeks];
  const itemListElement = allShown.map((w, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: `${w.card.name} — ${formatFriday(w.anchor)}`,
    url: `${siteUrl}/tarot-card/${w.card.slug}`,
  }));

  const faq = [
    {
      q: "What is Fate Friday",
      a: "It is a weekly featured tarot card published every Friday. The selection is deterministic and changes once per week at a fixed time.",
    },
    {
      q: "When does the card change",
      a: "The card changes on Friday at 09:00 Coordinated Universal Time. All visitors see the same feature for the entire week.",
    },
    {
      q: "How is the card chosen",
      a: "A simple seeded method uses the number of weeks since a fixed Friday in the past. The result is stable and does not require a database.",
    },
  ];

  const faqEntities = faq.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  }));

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}#org`,
        name: "TarotFate",
        url: siteUrl,
        logo: {
          "@type": "ImageObject",
          url: orgLogo,
          width: 512,
          height: 512,
        },
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}#website`,
        url: siteUrl,
        name: "TarotFate",
        publisher: { "@id": `${siteUrl}#org` },
        inLanguage: "en",
      },
      {
        "@type": "WebPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: "Fate Friday | Weekly Tarot Feature",
        description: metaDescription,
        breadcrumb: { "@id": `${pageUrl}#breadcrumbs` },
        isPartOf: { "@id": `${siteUrl}#website` },
        primaryImageOfPage: { "@id": `${pageUrl}#image` },
        inLanguage: "en",
      },
      {
        "@type": "ImageObject",
        "@id": `${pageUrl}#image`,
        url: ogImage,
        width: 1200,
        height: 630,
        caption: "TarotFate cover",
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
          { "@type": "ListItem", position: 2, name: "Fate Friday", item: pageUrl },
        ],
      },
      { "@type": "ItemList", "@id": `${pageUrl}#weeks`, name: "Fate Friday Weeks", itemListElement },
      { "@type": "FAQPage", "@id": `${pageUrl}#faq`, mainEntity: faqEntities },
    ],
  };
}

/* =========================================================
   Component
========================================================= */
export default function FateFriday() {
  const items = useMemo(
    () => Object.values(cardData).sort((a, b) => a.slug.localeCompare(b.slug)),
    []
  );
  const thisWeek = useMemo(() => pickThisWeek(items), [items]);
  const pastWeeks = useMemo(() => getPastWeeks(items, 8), [items]);

  // Long-form weekly note (cached per-week, 500–700 words)
  const [ff, setFf] = useState({ title: "", note: "" });
  const [ffLoading, setFfLoading] = useState(true);
  const [ffError, setFfError] = useState("");

  const ffPayload = useMemo(
    () => ({
      zodiac: "",
      theme: "reflection",
      tone: "gentle",
      cardName: thisWeek.card?.name || "",
      weekAnchorUTC: thisWeek.anchor?.toISOString() || "",
    }),
    [thisWeek.card?.name, thisWeek.anchor]
  );

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    // bump version to bust old short caches
    const cacheKey = `tf_ff_long_v2_${thisWeek.key}_${thisWeek.card?.slug || ""}`;

    async function run() {
      try {
        setFfLoading(true);
        setFfError("");

        // localStorage cache first (but only if ≥ 500 words)
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed?.note && wc(parsed.note) >= 500) {
            if (!cancelled) {
              setFf(parsed);
              setFfLoading(false);
              return;
            }
          } else {
            // stale/too-short cache → ignore
            localStorage.removeItem(cacheKey);
          }
        }

        const res = await fetch(`${API_BASE}/api/fatefriday`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(ffPayload),
          signal: controller.signal,
          keepalive: true,
        });
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);

        const payload = {
          title: data?.title || "Fate Friday",
          note: data?.note || [data?.intro, data?.guidance].filter(Boolean).join(" "),
        };

        if (wc(payload.note) < 500) {
          // enforce length on client as a last resort
          throw new Error("Guide was shorter than expected. Please refresh.");
        }

        setFf(payload);
        try {
          localStorage.setItem(cacheKey, JSON.stringify(payload));
        } catch {}
      } catch (e) {
        if (e?.name === "AbortError" || cancelled) return;
        setFfError(
          "Could not load this week’s Fate Friday guide. The card feature still works without it."
        );
      } finally {
        if (!cancelled) setFfLoading(false);
      }
    }

    run();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [ffPayload, thisWeek.key, thisWeek.card?.slug]);

  const heroImg = thisWeek.card?.image || `/card/${thisWeek.card?.slug}.webp`;

  return (
    <div
      className={`${FONT} text-white min-h-screen relative overflow-hidden`}
      style={{ background: CANVAS_BG }}
    >
      <Seo
        title="Fate Friday | Weekly Tarot Feature – TarotFate"
        description={metaDescription}
        url={pageUrl}
        image={ogImage}
        schema={schemaGraph(thisWeek, pastWeeks)}
        keywords={pageKeywords}
        includeMega={true}
      />

      {heroImg ? (
        <Helmet>
          <link
            rel="preload"
            as="image"
            href={heroImg}
            imagesizes="(min-width: 768px) 300px, 60vw"
          />
        </Helmet>
      ) : null}

      {/* Ambient auras */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[160px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
        <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[140px] bg-[radial-gradient(circle,rgba(140,59,90,0.55)_0%,transparent_70%)]" />
      </div>

      {/* HERO */}
      <section className={`pt-6 ${WRAP} ${MAXW} text-center`}>
        <h1 className="text-xl sm:text-2xl tracking-wide font-bold">Fate Friday</h1>
        <div className="flex items-center justify-center mb-1 mt-1">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
          <span className="px-4 text-[#f3eaff]/100">✦</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
        </div>
        <p className={`${TEXT} text-base mt-2`}>
          Weekly Tarot Focus: tarot card, meaning and one doable action.
        </p>
        <div>
          <span className="text-[14px] text-white/70 italic mt-3">
            Published: {formatFriday(thisWeek.anchor)}
          </span>
        </div>
      </section>

      {/* THIS WEEK */}
      <section id="this-week" className={`mt-4 ${WRAP} ${MAXW}`}>
        <div className={`${PANEL_BASE} ${SILK_TURQ} p-3 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <div className="grid md:grid-cols-[300px,1fr] gap-4 items-start">
              {/* Mobile title/subtitle ABOVE the card */}
              <div className="md:hidden text-center">
                <h2 className="text-lg font-semibold text-white/95">{thisWeek.card.name}</h2>
                {thisWeek.card.subtitle ? (
                  <p className="text-sm italic text-white/80">{thisWeek.card.subtitle}</p>
                ) : null}
              </div>

              {/* Image */}
              <Link
                to={`/tarot-card/${thisWeek.card.slug}`}
                className="block group rounded-xl border border-white/0 bg-white/0 hover:bg-white/10 hover:border-white/25 transition"
              >
                <img
                  src={heroImg}
                  alt={thisWeek.card.name}
                  className="rounded-xl w-full h-auto object-contain max-h-[260px] sm:max-h-[320px] md:max-h-[520px]"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = "/icons/logo.png";
                  }}
                />
              </Link>

              {/* Copy */}
              <div>
                <div className="hidden md:block">
                  <h2 className="mt-2 text-lg font-semibold text-white/95">{thisWeek.card.name}</h2>
                  {thisWeek.card.subtitle ? (
                    <p className="text-sm italic text-white/80">{thisWeek.card.subtitle}</p>
                  ) : null}
                </div>

                <p className={`${TEXT} text-[15px] mt-3`}>
                  Use this card as a guide for the week. Read the page for upright and
                  reversed meanings and translate one message into a small action you will
                  complete.
                </p>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Link
                    to={`/tarot-card/${thisWeek.card.slug}`}
                    className="px-3 py-1.5 rounded-full bg-white/10 border border-white/30 text-white/95 text-sm font-normal hover:bg-white/30 hover:border-white/40 transition"
                  >
                    Open full meaning
                  </Link>
                  <Link
                    to={`/tarot/${suitSlug(thisWeek.card.suit)}`}
                    className="px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/95 text-sm font-normal hover:bg-white/20 hover:border-white/30 transition"
                  >
                    Explore this suit
                  </Link>
                </div>

                {/* Fate Friday weekly note (long-form) */}
                <div
                  className="mt-6 rounded-md border border-white/25 bg-white/10 p-3"
                  role="status"
                  aria-live="polite"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-base font-semibold text-white/95">
                      {ffLoading ? "Fate Friday" : ff.title || "Fate Friday"}
                    </h3>
                  </div>

                  {ffError ? (
                    <p className="mt-2 text-[15px] text-white/80">{ffError}</p>
                  ) : ffLoading ? (
                    <p className="mt-2 text-[15px] text-white/75">Please wait a moment. Preparing your guide…</p>
                  ) : (
                    <p className="mt-2 text-sm text-white/85 whitespace-pre-line">{ff.note}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* top gleam */}
          <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
      </section>

      {/* RECENT WEEKS */}
      <section id="recent-weeks" className={`mt-6 ${WRAP} ${MAXW}`}>
        <div className={`${PANEL_BASE} ${SILK_TURQ} p-3 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h3 className="text-base sm:text-lg font-semibold text-white/95">✦ Recent Weeks</h3>
            <p className={`${TEXT} text-[15px] mt-2`}>
              Review the last selections and notice patterns that repeat. Use the images to
              anchor steady practice.
            </p>

            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 sm:gap-4">
              {pastWeeks.map(({ card, anchor, key }) => (
                <Link
                  key={key}
                  to={`/tarot-card/${card.slug}`}
                  className={[
                    "transition-all duration-100 ease-out will-change-transform",
                    "group rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 hover:border-white/25 hover:-translate-y-1",
                  ].join(" ")}
                >
                  <div className="p-2 flex flex-col items-center gap-1">
                    <img
                      src={card.image || `/card/${card.slug}.webp`}
                      alt={card.name}
                      loading="lazy"
                      decoding="async"
                      className="h-[180px] w-full object-cover rounded-lg select-none pointer-events-none"
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "/icons/logo.png";
                      }}
                    />
                    <div className="text-center">
                      <h4 className="text-sm font-semibold text-white/95">{card.name}</h4>
                      <p className="text-xs text-white/75">{formatFriday(anchor)}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
      </section>

      {/* METHOD */}
      <section id="method" className={`mt-6 ${WRAP} ${MAXW} mb-12`}>
        <div className={`${PANEL_BASE} ${SILK_TURQ} p-3 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h3 className="text-base sm:text-lg font-semibold text-white/95">✦ How the Card is Selected</h3>
            <p className={`${TEXT} text-[15px] mt-2`}>
              The weekly feature uses the number of weeks since a fixed Friday in the past
              as a seed. This method is stable and needs no database or server job. The
              update happens on Friday at nine in the morning Coordinated Universal Time
              and remains the same until the next Friday.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to="/what-is-tarot-cards-complete-guide"
                className="px-3 py-1.5 rounded-full bg-white/15 border border-white/20 text-white/95 text-sm font-medium hover:bg-white/25 hover:border-white/30 transition"
              >
                Learn tarot basics
              </Link>
              <Link
                to="/tarot-cards"
                className="px-3 py-1.5 rounded-full bg-white/15 border border-white/20 text-white/95 text-sm font-medium hover:bg-white/25 hover:border-white/30 transition"
              >
                Browse all cards
              </Link>
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
      </section>
    </div>
  );
}
